/**
 * API 封装
 *
 * 三条纪律：
 * 1. **服务端是唯一裁判**：分数/星级一律用服务端返回的结果，客户端本地算出来的只用于即时反馈。
 * 2. **弱网不崩**：所有调用返回 `ApiResult`，网络失败标记 `offline: true`，
 *    由上层降级成"本地先玩、联网再同步"，而不是弹一个错误框（GDD 8.3-5）。
 * 3. **不抛异常给 UI**：JSON 解析失败、超时、5xx 都收敛成结果对象。
 */

import type { Action, ChapterConfig, LevelConfig, SimResult } from "@chuandeng/shared";
import { readJson, writeJson, type KeyValueStore } from "../store/storage.js";

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; offline: boolean; status?: number };

export interface SessionUser {
  id: string;
  displayName: string;
  role: "player" | "teacher" | "admin";
}

export interface ChapterSummary {
  id: string;
  title: string;
  theme: string;
  description: string;
  levelCount: number;
}

export interface LevelProgress {
  levelId: string;
  chapterId: string;
  bestScore: number;
  stars: number;
}

export interface WrongAnswerRow {
  cardId: string;
  stage: number;
  reviewAt: number;
  due: boolean;
}

export interface ProgressSnapshot {
  user: SessionUser;
  levels: LevelProgress[];
  ownedCardIds: string[];
  wrongAnswers: WrongAnswerRow[];
  lampOil: number;
}

export interface TaskView {
  scope: "daily" | "weekly";
  slot: number;
  taskId: string;
  title: string;
  target: number;
  progress: number;
  rewardLampOil: number;
  claimed: boolean;
  completed: boolean;
}

export interface TaskBoard {
  dayKey: string;
  weekKey: string;
  daily: TaskView[];
  weekly: TaskView[];
  lampOil: number;
}

export interface SettlementResponse {
  levelId: string;
  outcome: "win" | "lose";
  score: number;
  stars: number;
  turns: number;
  mist: number;
  gainedCardIds: string[];
  newCardIds: string[];
  wrongCardIds: string[];
  correctAnswers: number;
  checksum: string;
  checksumMatched?: boolean;
}

export interface RunSnapshot {
  levelId: string;
  mapSeed: number;
  eventSeed: number;
  actions: Action[];
  updatedAt: number;
}

const TOKEN_KEY = "chuandeng.session.token";
const DEVICE_KEY = "chuandeng.device.id";

/** 客户端生成设备标识（与服务端 DEVICE_ID_PATTERN 对齐：8–64 位 URL 安全字符） */
function generateDeviceId(): string {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_";
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let out = "dev-";
  for (let i = 0; i < bytes.length; i++) out += alphabet[bytes[i]! % alphabet.length];
  return out;
}

export interface GameApiOptions {
  store: KeyValueStore;
  baseUrl?: string;
  timeoutMs?: number;
}

export class GameApi {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private lastOffline = false;

  constructor(private readonly opts: GameApiOptions) {
    this.baseUrl = (opts.baseUrl ?? "").replace(/\/$/, "");
    this.timeoutMs = opts.timeoutMs ?? 8000;
  }

  /** 上一次请求是否因为网络问题失败（用于顶部离线提示） */
  get offline(): boolean {
    return this.lastOffline;
  }

  get deviceId(): string {
    const existing = this.opts.store.get(DEVICE_KEY);
    if (existing && existing.length >= 8) return existing;
    const created = generateDeviceId();
    this.opts.store.set(DEVICE_KEY, created);
    return created;
  }

  get token(): string | null {
    return this.opts.store.get(TOKEN_KEY);
  }

  private setToken(token: string | null): void {
    if (token) this.opts.store.set(TOKEN_KEY, token);
    else this.opts.store.remove(TOKEN_KEY);
  }

  private async request<T>(
    path: string,
    init: RequestInit & { auth?: boolean } = {},
  ): Promise<ApiResult<T>> {
    const { auth = true, ...rest } = init;
    const headers = new Headers(rest.headers);
    headers.set("Content-Type", "application/json");
    if (auth && this.token) headers.set("Authorization", `Bearer ${this.token}`);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        ...rest,
        headers,
        signal: controller.signal,
      });
      this.lastOffline = false;

      const text = await response.text();
      let body: unknown = null;
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          body = null;
        }
      }

      if (!response.ok) {
        const error =
          body && typeof body === "object" && "error" in body
            ? String((body as { error: unknown }).error)
            : `http_${response.status}`;
        return { ok: false, error, offline: false, status: response.status };
      }
      return { ok: true, data: body as T };
    } catch {
      this.lastOffline = true;
      return { ok: false, error: "offline", offline: true };
    } finally {
      clearTimeout(timer);
    }
  }

  /** 游客开局；已有令牌时直接复用（先 GET /v1/me 探一下） */
  async ensureSession(): Promise<ApiResult<SessionUser>> {
    if (this.token) {
      const me = await this.request<{ user: SessionUser | null }>("/v1/me");
      if (me.ok && me.data.user) return { ok: true, data: me.data.user };
      if (!me.ok && me.offline) return me; // 离线：保留旧令牌，先本地玩
      this.setToken(null); // 令牌失效（服务端重启/KV 过期）→ 重新注册
    }

    const res = await this.request<{ token: string; user: SessionUser }>("/v1/auth/guest", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ deviceId: this.deviceId }),
    });
    if (!res.ok) return res;
    this.setToken(res.data.token);
    return { ok: true, data: res.data.user };
  }

  async logOut(): Promise<void> {
    await this.request("/v1/auth/logout", { method: "POST" });
    this.setToken(null);
  }

  listChapters(): Promise<ApiResult<{ contentVersion: string; chapters: ChapterSummary[] }>> {
    return this.request("/v1/levels", { auth: false });
  }

  getChapter(chapterId: string): Promise<ApiResult<{ contentVersion: string; chapter: ChapterConfig }>> {
    return this.request(`/v1/levels/chapter/${encodeURIComponent(chapterId)}`, { auth: false });
  }

  getLevel(levelId: string): Promise<ApiResult<{ contentVersion: string; level: LevelConfig }>> {
    return this.request(`/v1/levels/${encodeURIComponent(levelId)}`, { auth: false });
  }

  fetchProgress(): Promise<ApiResult<ProgressSnapshot>> {
    return this.request("/v1/progress");
  }

  submitProgress(payload: {
    levelId: string;
    mapSeed: number;
    eventSeed: number;
    actions: readonly Action[];
    clientChecksum?: string;
  }): Promise<ApiResult<SettlementResponse>> {
    return this.request("/v1/progress", { method: "POST", body: JSON.stringify(payload) });
  }

  saveRun(levelId: string, mapSeed: number, eventSeed: number, actions: readonly Action[]) {
    return this.request<{ ok: boolean }>(`/v1/runs/${encodeURIComponent(levelId)}`, {
      method: "PUT",
      body: JSON.stringify({ mapSeed, eventSeed, actions }),
    });
  }

  loadRun(levelId: string): Promise<ApiResult<RunSnapshot>> {
    return this.request(`/v1/runs/${encodeURIComponent(levelId)}`);
  }

  fetchTasks(): Promise<ApiResult<TaskBoard>> {
    return this.request("/v1/tasks");
  }

  claimTask(scope: "daily" | "weekly", slot: number): Promise<ApiResult<{ lampOil: number }>> {
    return this.request("/v1/tasks/claim", {
      method: "POST",
      body: JSON.stringify({ scope, slot }),
    });
  }

  resolveWrongAnswer(
    cardId: string,
    correct: boolean,
  ): Promise<ApiResult<{ cleared: boolean; stage: number; reviewAt: number }>> {
    return this.request("/v1/wrong-answers/resolve", {
      method: "POST",
      body: JSON.stringify({ cardId, correct }),
    });
  }

  syncWrongAnswers(cardIds: readonly string[]): Promise<ApiResult<{ wrongAnswers: WrongAnswerRow[] }>> {
    return this.request("/v1/wrong-answers/sync", {
      method: "POST",
      body: JSON.stringify({ cardIds }),
    });
  }

  markCardsRead(cardIds: readonly string[]): Promise<ApiResult<{ ok: boolean }>> {
    return this.request("/v1/cards/read", {
      method: "PUT",
      body: JSON.stringify({ cardIds }),
    });
  }
}

// ---------- 本地缓存（离线也能看到上次的进度） ----------

const CACHE_KEY = "chuandeng.cache.progress";

export function cacheProgress(store: KeyValueStore, snapshot: ProgressSnapshot): void {
  writeJson(store, CACHE_KEY, snapshot);
}

export function readCachedProgress(store: KeyValueStore): ProgressSnapshot | null {
  return readJson<ProgressSnapshot | null>(store, CACHE_KEY, null);
}
